import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  const scrollToBooking = () => {
    const bookingSection = document.getElementById('booking-section');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/0e7dd5281691a500d2831b4632254052.jpg"
            alt="Modern Medical Clinic Interior"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/30 to-black/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-emerald-500/90 text-white px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
                <i className="ri-checkbox-circle-fill"></i>
                <span className="font-medium text-sm whitespace-nowrap">Accepting New Patients</span>
              </div>
              
              <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                Your Trusted Family Doctor in Canada
              </h1>
              
              <p className="text-xl text-gray-100 mb-8 leading-relaxed">
                Providing compassionate, comprehensive healthcare with a patient-centered approach. Your health and well-being are our top priorities.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <button 
                  onClick={scrollToBooking}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Book Appointment
                </button>
                <Link 
                  to="/new-patients"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap"
                >
                  New Patients Info
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-check-line text-emerald-400 text-xl"></i>
                  <span>Same-Day Appointments</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-video-line text-emerald-400 text-xl"></i>
                  <span>Telehealth Available</span>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900">Office Hours</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-700 font-medium">Monday - Friday</span>
                    <span className="text-gray-900 font-semibold">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-700 font-medium">Saturday</span>
                    <span className="text-gray-900 font-semibold">10:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-700 font-medium">Sunday</span>
                    <span className="text-gray-600">Closed</span>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-phone-fill text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Call Us</p>
                      <a href="tel:+14165551234" className="text-lg font-semibold text-blue-600 hover:text-blue-700">
                        (416) 555-1234
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-map-pin-fill text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Location</p>
                      <p className="text-gray-900 font-medium">123 Medical Centre Dr<br/>Toronto, ON M5H 2N2</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Care Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Providing a full range of medical services to keep you and your family healthy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-heart-pulse-line',
                title: 'General Health Checkups',
                description: 'Comprehensive physical examinations and routine health screenings to maintain your overall wellness and detect issues early.'
              },
              {
                icon: 'ri-medicine-bottle-line',
                title: 'Chronic Disease Management',
                description: 'Expert care for diabetes, hypertension, and other chronic conditions with personalized treatment plans and ongoing support.'
              },
              {
                icon: 'ri-syringe-line',
                title: 'Vaccinations & Immunizations',
                description: 'Complete vaccination services for all ages, including flu shots, travel vaccines, and childhood immunization schedules.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Preventive Care Programs',
                description: 'Proactive health strategies including lifestyle counseling, nutrition guidance, and disease prevention planning.'
              },
              {
                icon: 'ri-surgical-mask-line',
                title: 'Minor Procedures',
                description: 'In-office minor surgical procedures, wound care, suture removal, and other medical treatments in a comfortable setting.'
              },
              {
                icon: 'ri-hospital-line',
                title: 'Specialist Referrals',
                description: 'Coordinated referrals to trusted specialists when advanced care is needed, ensuring continuity of your treatment.'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-6 mx-auto">
                  <i className={`${service.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-center mb-4">
                  {service.description}
                </p>
                <div className="text-center">
                  <Link 
                    to="/services"
                    className="inline-flex items-center gap-2 text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    Learn More
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services"
              className="inline-block bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20confident%20Canadian%20doctor%20in%20white%20medical%20coat%20with%20stethoscope%2C%20warm%20friendly%20smile%2C%20standing%20in%20modern%20bright%20clinic%20office%2C%20natural%20lighting%2C%20professional%20medical%20portrait%2C%20trustworthy%20healthcare%20provider%2C%20clean%20background%20with%20medical%20equipment%2C%20approachable%20demeanor&width=600&height=800&seq=doctor-portrait-main&orientation=portrait"
                  alt="Professional Doctor Portrait"
                  className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
                />
                
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full">
                        <span className="text-2xl font-bold text-blue-900">15+</span>
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Years of Experience</p>
                      <p className="text-sm text-gray-600">Serving the Community</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mt-12">
                {[
                  { icon: 'ri-graduation-cap-line', label: 'Board Certified' },
                  { icon: 'ri-award-line', label: 'Excellence Award' },
                  { icon: 'ri-user-heart-line', label: '5000+ Patients' },
                  { icon: 'ri-global-line', label: '3 Languages' }
                ].map((badge, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full shadow-md mx-auto mb-2">
                      <i className={`${badge.icon} text-2xl text-blue-600`}></i>
                    </div>
                    <p className="text-xs text-gray-700 font-medium">{badge.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-3">
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Meet Your Doctor
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p className="text-lg">
                  With over 15 years of experience in family medicine, I am dedicated to providing compassionate, comprehensive healthcare to patients of all ages. My approach combines evidence-based medicine with a deep understanding of each patient's unique needs and circumstances.
                </p>
                <p className="text-lg">
                  I believe in building long-term relationships with my patients, taking the time to listen, understand, and develop personalized care plans that promote optimal health and well-being. Whether you need preventive care, management of chronic conditions, or treatment for acute illnesses, I'm here to support you every step of the way.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Our Clinic</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {[
                    {
                      icon: 'ri-user-heart-line',
                      title: 'Patient-Centered Care',
                      description: 'Your health goals and concerns guide every decision'
                    },
                    {
                      icon: 'ri-microscope-line',
                      title: 'Evidence-Based Medicine',
                      description: 'Latest research and proven treatment methods'
                    },
                    {
                      icon: 'ri-heart-line',
                      title: 'Comprehensive Approach',
                      description: 'Treating the whole person, not just symptoms'
                    },
                    {
                      icon: 'ri-chat-smile-3-line',
                      title: 'Accessible Communication',
                      description: 'Easy to reach and always ready to listen'
                    }
                  ].map((point, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                          <i className={`${point.icon} text-xl text-blue-600`}></i>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{point.title}</h4>
                        <p className="text-sm text-gray-600">{point.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Link 
                to="/about"
                className="inline-block bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap"
              >
                Learn More About Me
              </Link>
            </div>
          </div>

          <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
            <div className="flex flex-wrap justify-around items-center gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-900 mb-2">15+</div>
                <div className="text-gray-600">Years Experience</div>
              </div>
              <div className="hidden md:block w-px h-16 bg-gray-300"></div>
              <div>
                <div className="text-4xl font-bold text-blue-900 mb-2">5000+</div>
                <div className="text-gray-600">Patients Served</div>
              </div>
              <div className="hidden md:block w-px h-16 bg-gray-300"></div>
              <div>
                <div className="text-4xl font-bold text-blue-900 mb-2">3</div>
                <div className="text-gray-600">Languages Spoken</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Booking Section */}
      <section id="booking-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="bg-blue-900 rounded-2xl p-12 text-white">
              <h2 className="font-serif text-4xl font-bold mb-6">
                Book Your Appointment
              </h2>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Same-day appointments available',
                  'Online booking confirmation',
                  'Flexible scheduling options',
                  'Telehealth consultations offered',
                  'Easy rescheduling if needed'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-emerald-400 text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex gap-4 mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Happy%20satisfied%20patient%20smiling%20portrait%2C%20diverse%20Canadian%20person%2C%20professional%20headshot%2C%20warm%20friendly%20expression%2C%20simple%20clean%20background&width=80&height=80&seq=patient-testimonial-avatar&orientation=squarish"
                    alt="Patient"
                    className="w-16 h-16 rounded-full object-cover object-top"
                  />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400"></i>
                      ))}
                    </div>
                    <p className="text-sm italic mb-2">
                      "Excellent care and very professional. The doctor takes time to listen and explain everything clearly. Highly recommend!"
                    </p>
                    <p className="text-sm font-semibold">- Sarah M.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <form 
                data-readdy-form
                action="https://readdy.ai/api/form/d5fc8b310k7tbp4q4af0"
                method="POST"
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  // Validation
                  const name = formData.get('name') as string;
                  const phone = formData.get('phone') as string;
                  const email = formData.get('email') as string;
                  const healthCard = formData.get('health_card') as string;
                  
                  if (!name || !phone || !email || !healthCard) {
                    alert('Please fill in all required fields');
                    return;
                  }

                  // Submit
                  fetch('https://readdy.ai/api/form/d5fc8b310k7tbp4q4af0', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Appointment request submitted successfully! We will contact you shortly to confirm.');
                      form.reset();
                    } else {
                      alert('There was an error submitting your request. Please try again.');
                    }
                  })
                  .catch(() => {
                    alert('There was an error submitting your request. Please try again.');
                  });
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Appointment Type <span className="text-red-500">*</span>
                  </label>
                  <select 
                    name="appointment_type"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select appointment type</option>
                    <option value="New Patient">New Patient Visit</option>
                    <option value="Follow-up">Follow-up Appointment</option>
                    <option value="General Checkup">General Checkup</option>
                    <option value="Chronic Disease Management">Chronic Disease Management</option>
                    <option value="Vaccination">Vaccination</option>
                    <option value="Telehealth">Telehealth Consultation</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Date <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="date"
                      name="preferred_date"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Time <span className="text-red-500">*</span>
                    </label>
                    <select 
                      name="preferred_time"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select time</option>
                      <option value="9:00 AM">9:00 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="1:00 PM">1:00 PM</option>
                      <option value="2:00 PM">2:00 PM</option>
                      <option value="3:00 PM">3:00 PM</option>
                      <option value="4:00 PM">4:00 PM</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text"
                    name="name"
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="tel"
                      name="phone"
                      required
                      placeholder="(416) 555-1234"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Health Card Number <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text"
                    name="health_card"
                    required
                    placeholder="Enter your health card number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Reason for Visit (Optional)
                  </label>
                  <textarea 
                    name="reason"
                    rows={3}
                    maxLength={500}
                    placeholder="Brief description of your health concern..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Confirm Appointment
                </button>

                <p className="text-sm text-gray-600 text-center">
                  You'll receive confirmation via email and SMS
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map and Contact Section */}
      <section className="relative h-screen min-h-[600px]">
        <div className="absolute inset-0">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.267837056524!2d-79.38318708450221!3d43.65322097912108!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b34d68bf33a9b%3A0x15edd8c4de1c7581!2sCN%20Tower!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Clinic Location Map"
          ></iframe>
        </div>

        <div className="absolute bottom-8 left-8 bg-white rounded-2xl shadow-2xl p-8 max-w-md z-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Visit Our Clinic</h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-map-pin-fill text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">123 Medical Centre Drive</p>
                <p className="text-gray-600">Toronto, ON M5H 2N2, Canada</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-phone-fill text-blue-600 text-xl"></i>
              </div>
              <div>
                <a href="tel:+14165551234" className="text-lg font-semibold text-blue-600 hover:text-blue-700">
                  (416) 555-1234
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-mail-fill text-blue-600 text-xl"></i>
              </div>
              <div>
                <a href="mailto:info@doctorclinic.ca" className="text-blue-600 hover:text-blue-700">
                  info@doctorclinic.ca
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Office Hours</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">Mon - Fri</span>
                <span className="font-semibold text-gray-900">9:00 AM - 5:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Saturday</span>
                <span className="font-semibold text-gray-900">10:00 AM - 2:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Sunday</span>
                <span className="text-gray-600">Closed</span>
              </div>
            </div>
          </div>

          <a 
            href="https://www.google.com/maps/dir//123+Medical+Centre+Drive+Toronto+ON+M5H+2N2"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-blue-900 hover:bg-blue-800 text-white text-center py-3 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap"
          >
            Get Directions
          </a>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4">
              Send Us a Message
            </h2>
            <p className="text-lg text-gray-600">
              Have a question? We're here to help. Fill out the form below and we'll get back to you soon.
            </p>
          </div>

          <form 
            data-readdy-form
            action="https://readdy.ai/api/form/d5fbh7k7bv8ec1oguhkg"
            method="POST"
            className="bg-gray-50 rounded-2xl p-8 space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const phone = formData.get('phone') as string;
              const message = formData.get('message') as string;
              
              if (!name || !email || !phone || !message) {
                alert('Please fill in all required fields');
                return;
              }

              fetch('https://readdy.ai/api/form/d5fbh7k7bv8ec1oguhkg', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData as any).toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('Message sent successfully! We will get back to you soon.');
                  form.reset();
                } else {
                  alert('There was an error sending your message. Please try again.');
                }
              })
              .catch(() => {
                alert('There was an error sending your message. Please try again.');
              });
            }}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text"
                  name="name"
                  required
                  placeholder="Your name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input 
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input 
                type="tel"
                name="phone"
                required
                placeholder="(416) 555-1234"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea 
                name="message"
                rows={5}
                required
                maxLength={500}
                placeholder="How can we help you?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>

            <button 
              type="submit"
              className="w-full bg-blue-900 hover:bg-blue-800 text-white py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Send Message
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
